/*
 * Lily & Minx - help pages: Contact and policies (lm-help)
 * LAST UPDATED: 31/10/2026
 * ---------------------------------------------------------------------------
 * Progressive enhancement for the lm-help sections. Every page reads and works
 * without it. Loaded as a module (deferred) by the sections that use it.
 *
 * - <lm-policy-page> (sections/lm-policy.liquid): a contents link, a link in a
 *   section's text or a URL hash that points at a policy section opens that
 *   <details> and moves focus to its summary, so keyboard and screen reader
 *   users land on the section they asked for. From 1000px, where the contents
 *   are pinned beside the text, it marks the entry being read with
 *   aria-current="true" (lm-help.css draws a rule beside it), using
 *   IntersectionObserver; below 1000px, or without it, nothing is marked.
 * - <lm-cookie-preferences> (Privacy only): shows its button when the page
 *   has the site's own cookie banner (<lm-consent>, module lm-consent) and
 *   opens its Cookie settings with the lm:consent:open event. Without it, the
 *   button shows only when Shopify's cookie banner API
 *   (window.privacyBanner.showPreferences) exists, and opens that banner's
 *   preferences. With neither, it stays hidden.
 * - <lm-contact-form> (sections/lm-contact.liquid and lm-contact-form.liquid,
 *   around snippets/lm-help-contact-form.liquid): a link such as
 *   /pages/contact?topic=return#contact-form preselects the topic whose line
 *   has that code, only when such a topic exists and the visitor has not
 *   picked one yet; after a post, focus moves to the success or error notice
 *   so screen readers announce it. Shopify only echoes email and message after
 *   a failed post, so the topic, order number and name are kept in
 *   sessionStorage for this tab when the form is sent, put back when the error
 *   notice shows, and removed on any other load (WCAG 3.3.7 Redundant Entry).
 *   The form still posts natively (hCaptcha).
 *   1.9.0, Which product?: shown for the product and sizing topics, a
 *   catalogue search as you type (an ARIA combobox), a pasted product link
 *   or ?product=<handle>[&variant=<id>|&size=<size>] from a product page
 *   becomes the chosen product with its sizes (LmContactProduct).
 *
 * Each element is defined once, cleans up when removed, and works again after
 * the theme editor re-renders a section.
 */

/**
 * The element a "#anchor" points at, if it is inside root.
 * @param {HTMLElement} root
 * @param {string | null} hash
 * @returns {HTMLElement | null}
 */
const targetForHash = (root, hash) => {
  if (!hash || !hash.startsWith('#') || hash.length < 2) return null;

  let id;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    return null;
  }

  const target = document.getElementById(id);
  return target && root.contains(target) ? target : null;
};

/**
 * The policy section a "#anchor" points at, if it is a <details> inside root.
 * @param {HTMLElement} root
 * @param {string | null} hash
 * @returns {HTMLDetailsElement | null}
 */
const detailsForHash = (root, hash) => {
  const target = targetForHash(root, hash);
  return target instanceof HTMLDetailsElement ? target : null;
};

/* The policy's desktop layout (contents pinned beside the text) starts at
   Prestige's 1000px breakpoint, as in lm-help.css section 4. */
const WIDE_QUERY = '(min-width: 1000px)';

/* The entry being read is the last one whose top has passed the reading line,
   30% down the window. The observer's box runs from far above the window down
   to that line, so a target counts as passed however far above it has
   scrolled, and a jump (Home, End, a hash) still reports every change. */
const READING_LINE_MARGIN = '1000000px 0px -70% 0px';

/**
 * An observer whose root is this document's own viewport. With the implicit
 * root, browsers ignore rootMargin inside a cross-origin iframe (the theme
 * editor's preview), so the reading line would not apply there. A browser
 * that does not take a Document root throws: it keeps the implicit root,
 * which is the same viewport on the storefront.
 * @param {IntersectionObserverCallback} callback
 * @param {IntersectionObserverInit} options
 * @returns {IntersectionObserver}
 */
const viewportObserver = (callback, options) => {
  try {
    return new IntersectionObserver(callback, { ...options, root: document });
  } catch {
    return new IntersectionObserver(callback, options);
  }
};

/**
 * @typedef {object} PolicySpy
 * @property {HTMLElement} aside
 * @property {HTMLAnchorElement[]} links
 * @property {HTMLElement[]} targets
 * @property {Set<Element>} passed targets whose top is above the reading line
 * @property {IntersectionObserver[]} observers
 * @property {AbortController | null} endWatch scroll listener, only while the last target is wholly in view
 * @property {HTMLAnchorElement | null} current
 * @property {number} frame
 */

class LmPolicyPage extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {PolicySpy | null} */
  #spy = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    window.addEventListener('hashchange', () => this.#reveal(window.location.hash), { signal });

    const wide = window.matchMedia(WIDE_QUERY);
    const syncSpy = () => (wide.matches ? this.#startSpy() : this.#stopSpy());
    wide.addEventListener('change', syncSpy, { signal });
    syncSpy();

    /* Page opened at /pages/policies/privacy#cookies: open the section now, so the
       browser's own scroll to the fragment lands on open content. Chrome clears focus
       when it processes that fragment, so move focus only after load, and only if the
       visitor has not focused something else in the meantime. */
    const details = detailsForHash(this, window.location.hash);
    if (!details) return;

    details.open = true;
    const focusOnLoad = () => {
      requestAnimationFrame(() => {
        if (document.activeElement === document.body || document.activeElement === null) this.#focusSummary(details);
      });
    };
    if (document.readyState === 'complete') focusOnLoad();
    else window.addEventListener('load', focusOnLoad, { once: true, signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#stopSpy();
  }

  /* Mark the contents entry being read. Needs the desktop aside and two or more
     entries whose targets exist; otherwise the page stays as it is. */
  #startSpy() {
    if (this.#spy || typeof IntersectionObserver !== 'function') return;

    const aside = this.querySelector('.lm-policy__aside');
    if (!(aside instanceof HTMLElement)) return;

    /** @type {HTMLAnchorElement[]} */
    const links = [];
    /** @type {HTMLElement[]} */
    const targets = [];
    for (const link of aside.querySelectorAll('.lm-policy__contents-list a[href^="#"]')) {
      const target = targetForHash(this, link.getAttribute('href'));
      if (!(link instanceof HTMLAnchorElement) || !target || targets.includes(target)) continue;
      links.push(link);
      targets.push(target);
    }
    if (targets.length < 2) return;

    /** @type {PolicySpy} */
    const spy = { aside, links, targets, passed: new Set(), observers: [], endWatch: null, current: null, frame: 0 };
    const queue = () => {
      if (spy.frame) return;
      spy.frame = requestAnimationFrame(() => {
        spy.frame = 0;
        this.#spyUpdate(spy);
      });
    };

    const line = viewportObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) spy.passed.add(entry.target);
        else spy.passed.delete(entry.target);
      }
      queue();
    }, { rootMargin: READING_LINE_MARGIN });
    for (const target of targets) line.observe(target);

    /* Near the end of the page the last target (Who we are) may never reach the
       reading line. While it is wholly in view, watch for the end of the page. */
    const end = viewportObserver((entries) => {
      const whole = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.99);
      if (whole && !spy.endWatch) {
        spy.endWatch = new AbortController();
        window.addEventListener('scroll', queue, { passive: true, signal: spy.endWatch.signal });
      } else if (!whole && spy.endWatch) {
        spy.endWatch.abort();
        spy.endWatch = null;
      }
      queue();
    }, { threshold: [0, 1] });
    end.observe(targets[targets.length - 1]);

    spy.observers = [line, end];
    this.#spy = spy;
  }

  #stopSpy() {
    const spy = this.#spy;
    if (!spy) return;

    this.#spy = null;
    for (const observer of spy.observers) observer.disconnect();
    spy.endWatch?.abort();
    cancelAnimationFrame(spy.frame);
    for (const link of spy.links) link.removeAttribute('aria-current');
  }

  /** @param {PolicySpy} spy */
  #spyUpdate(spy) {
    if (this.#spy !== spy) return;

    let index = 0;
    spy.targets.forEach((target, i) => {
      if (spy.passed.has(target)) index = i;
    });

    /* Scrolled to the end with the last target in view: that is the one being read. */
    const root = document.documentElement;
    if (spy.endWatch && window.scrollY > 0 && window.scrollY + window.innerHeight >= root.scrollHeight - 2) {
      index = spy.targets.length - 1;
    }

    const link = spy.links[index];
    if (link === spy.current) return;

    spy.current?.removeAttribute('aria-current');
    link.setAttribute('aria-current', 'true');
    spy.current = link;
    this.#keepInAside(spy.aside, link, index === 0);
  }

  /**
   * A long contents list scrolls inside the pinned aside: keep the marked entry
   * in its view (with the first entry, the list's heading too). The aside's own
   * scroll only, never the window's.
   * @param {HTMLElement} aside
   * @param {HTMLElement} link
   * @param {boolean} first
   */
  #keepInAside(aside, link, first) {
    if (aside.scrollHeight <= aside.clientHeight) return;
    if (first) {
      aside.scrollTop = 0;
      return;
    }

    const box = aside.getBoundingClientRect();
    const item = link.getBoundingClientRect();
    if (item.top < box.top) aside.scrollTop -= box.top - item.top;
    else if (item.bottom > box.bottom) aside.scrollTop += item.bottom - box.bottom;
  }

  /**
   * A click on the link already in the URL does not fire hashchange, so open the
   * section here as well. The browser still follows the link as usual (URL and scroll).
   * @param {MouseEvent} event
   */
  #onClick(event) {
    /* A modified or middle click opens the link elsewhere (new tab or window):
       leave this page's sections and focus alone. */
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const details = detailsForHash(this, link.getAttribute('href'));
    if (!details) return;

    details.open = true;
    requestAnimationFrame(() => this.#focusSummary(details));
  }

  /**
   * The URL hash changed (a link anywhere on the page, or history navigation).
   * @param {string} hash
   */
  #reveal(hash) {
    const details = detailsForHash(this, hash);
    if (!details) return;

    details.open = true;
    this.#focusSummary(details);
  }

  /** @param {HTMLDetailsElement} details */
  #focusSummary(details) {
    const summary = details.querySelector(':scope > summary');
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  }
}

/* The site's own cookie banner (module lm-consent, in the overlay group): its
   element opens Cookie settings when this event reaches the document. */
const SITE_CONSENT = 'lm-consent';
const SITE_CONSENT_OPEN = 'lm:consent:open';

/**
 * Whether this page has the site's own cookie banner, ready to open: an
 * <lm-consent> element that lm-consent.js has defined, so the event has a
 * listener. It comes before Shopify's banner.
 * @returns {boolean}
 */
const siteConsent = () => customElements.get(SITE_CONSENT) !== undefined && document.querySelector(SITE_CONSENT) !== null;

/**
 * Shopify's cookie banner API, when the store's banner is on and loaded: the
 * fallback on a page without the site's own banner.
 * @returns {{ showPreferences: () => Promise<unknown> } | null}
 */
const cookieBanner = () => {
  const banner = window.privacyBanner;
  return banner && typeof banner.showPreferences === 'function' ? banner : null;
};

class LmCookiePreferences extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {number} */
  #timer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.querySelector('button')?.addEventListener('click', () => this.#open(), { signal });

    if (this.#sync()) return;

    /* Check again once lm-consent is defined, in case its script ever runs
       after this one (today the overlay group comes before the page content),
       and, because Shopify's banner script loads asynchronously, on load and
       for a few seconds; then leave the button hidden. */
    customElements.whenDefined(SITE_CONSENT).then(() => {
      if (!signal.aborted) this.#sync();
    });
    window.addEventListener('load', () => this.#sync(), { once: true, signal });
    let tries = 0;
    this.#timer = window.setInterval(() => {
      tries += 1;
      if (this.#sync() || tries >= 10) this.#stop();
    }, 500);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#stop();
  }

  /** @returns {boolean} whether either banner can open (and the button is shown) */
  #sync() {
    const available = siteConsent() || cookieBanner() !== null;
    this.hidden = !available;
    if (available) this.#stop();
    return available;
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  async #open() {
    if (siteConsent()) {
      /* lm-consent gives focus back, on close, to what had it when it opened.
         Safari does not focus a clicked button, so focus it first. */
      this.querySelector('button')?.focus({ preventScroll: true });
      document.dispatchEvent(new Event(SITE_CONSENT_OPEN));
      return;
    }

    const banner = cookieBanner();
    if (!banner) return;

    try {
      await banner.showPreferences();
    } catch (error) {
      console.error('lm-help: the cookie preferences did not open', error);
    }
  }
}

/**
 * The topic code a link asked for (?topic=return), in lower case, or ''.
 * @returns {string}
 */
const requestedTopic = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.get('topic') ?? '').trim().toLowerCase();
};

/* Fields Shopify does not echo back after a failed post (it echoes email and
   message), kept for this tab between sending the form and the error notice. */
const KEPT_STORAGE_KEY = 'lm-help:contact-form';
const KEPT_FIELDS = ['contact[Topic]', 'contact[Order number]', 'contact[Product]', 'contact[Product link]', 'contact[name]'];
/* The size select only has its options once the kept product is looked up
   again, so its value is kept apart and handed to the product field. */
const KEPT_SIZE = 'contact[Size]';

/**
 * sessionStorage, or null where the browser blocks it (private modes, settings).
 * @returns {Storage | null}
 */
const tabStorage = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/* ---- Which product? (1.9.0) ---------------------------------------------- */

const PRODUCT_HANDLE = /^[a-z0-9][a-z0-9-]*$/;
const SEARCH_DELAY = 250;
const SEARCH_LIMIT = 6;

const routesRoot = () => window.Shopify?.routes?.root ?? '/';

/**
 * The product handle in a pasted link or path (.../products/<handle>...), or ''.
 * @param {string} text
 * @returns {string}
 */
const handleFromLink = (text) => {
  const match = /\/products\/([a-z0-9][a-z0-9-]*)/i.exec(text);
  return match ? match[1].toLowerCase() : '';
};

/**
 * What a product page link asked for: ?product=<handle>, optionally &variant=<id>
 * or &size=<size>. Anything malformed is ignored.
 * @returns {{ handle: string, variant: string, size: string }}
 */
const requestedProduct = () => {
  const params = new URLSearchParams(window.location.search);
  const handle = (params.get('product') ?? '').trim().toLowerCase();
  const variant = (params.get('variant') ?? '').trim();
  return {
    handle: PRODUCT_HANDLE.test(handle) ? handle : '',
    variant: /^\d{1,20}$/.test(variant) ? variant : '',
    size: (params.get('size') ?? '').trim().slice(0, 40),
  };
};

/**
 * A Shopify CDN image at a given width, or '' for anything else.
 * @param {unknown} src
 * @param {number} width
 * @returns {string}
 */
const imageAt = (src, width) => {
  if (typeof src !== 'string' || src === '') return '';
  try {
    const url = new URL(src, window.location.href);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
    url.searchParams.set('width', String(width));
    return url.href;
  } catch {
    return '';
  }
};

/**
 * The contact form's Which product? field (snippets/lm-help-contact-form.liquid):
 * shown for the topics in data-topics, required for those in data-required-topics.
 * Typing searches the catalogue (Shopify's /search/suggest.json) in an ARIA
 * combobox; a pasted product link, a ?product= link from a product page or a
 * kept value after a failed post is looked up by handle (/products/<handle>.js).
 * A chosen product shows as a card with a Change button, fills the hidden
 * contact[Product link] and lists its sizes in contact[Size]. Free text that
 * matches nothing is sent as typed.
 */
class LmContactProduct {
  /**
   * @param {HTMLElement} root - [data-lm-product-field]
   * @param {HTMLSelectElement | null} topic - the topic select, if any
   * @param {AbortSignal} signal - aborted when the form leaves the page
   */
  constructor(root, topic, signal) {
    this.root = root;
    this.topic = topic;
    this.signal = signal;
    this.topics = (root.dataset.topics ?? '').split(/\s+/).filter(Boolean);
    this.requiredTopics = (root.dataset.requiredTopics ?? '').split(/\s+/).filter(Boolean);

    const find = (selector) => root.querySelector(selector);
    this.search = find('[data-lm-product-search]');
    this.input = find('[data-lm-product-input]');
    this.list = find('[data-lm-product-list]');
    this.status = find('[data-lm-product-status]');
    this.requiredMark = find('[data-lm-product-required]');
    this.chosen = find('[data-lm-product-chosen]');
    this.image = find('[data-lm-product-image]');
    this.title = find('[data-lm-product-title]');
    this.change = find('[data-lm-product-change]');
    this.link = find('[data-lm-product-link]');
    this.sizeField = find('[data-lm-size-field]');
    this.size = find('[data-lm-size]');

    /** @type {Array<{ handle: string, title: string, image: string, available: boolean }>} */
    this.results = [];
    this.active = -1;
    this.timer = 0;
    /** @type {AbortController | null} - the latest search; a newer search cancels it */
    this.searchRequest = null;
    /** @type {AbortController | null} - the latest product lookup; only a newer lookup cancels it */
    this.loadRequest = null;
    this.hasProduct = false;
    /** @type {any} - the chosen product's /products/<handle>.js */
    this.product = null;
    this.handle = '';
    /** @type {any} - the variant a product page link named, whose other options (colour) the size keeps */
    this.baseVariant = null;

    if (!(this.input instanceof HTMLInputElement) || !(this.list instanceof HTMLElement)) return;

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.list.id);

    this.input.addEventListener('input', () => this.#onInput(), { signal });
    this.input.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.input.addEventListener('blur', () => window.setTimeout(() => this.#close(), 150), { signal });
    /* pointerdown would blur the input before the click lands. */
    this.list.addEventListener('pointerdown', (event) => event.preventDefault(), { signal });
    this.list.addEventListener('click', (event) => {
      const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
      if (option instanceof HTMLElement) this.#pick(Number(option.dataset.index), true);
    }, { signal });
    this.change?.addEventListener('click', () => this.#clear(true), { signal });
    this.topic?.addEventListener('change', () => this.#sync(), { signal });
    signal.addEventListener('abort', () => {
      window.clearTimeout(this.timer);
      this.searchRequest?.abort();
      this.loadRequest?.abort();
    });

    this.size?.addEventListener('change', () => this.#linkSize(), { signal });
    /* "Not a particular size" is no size: leave it out of the email. Submit only
       fires once the form is valid, after lm-help.js has kept the values. */
    root.closest('form')?.addEventListener('submit', () => {
      if (this.size instanceof HTMLSelectElement && this.size.value === '') this.size.disabled = true;
    }, { signal });
    /* Back to this page from the browser's cache: enable it again. */
    window.addEventListener('pageshow', () => this.#sync(), { signal });
  }

  /**
   * Show or hide the field for the current topic and look up a product that a
   * link asked for or that was kept after a failed post.
   * @param {{ handle: string, variant: string, size: string }} wanted
   */
  start(wanted) {
    this.#sync();
    const keptLink = this.link instanceof HTMLInputElement ? this.link.value : '';
    const kept = handleFromLink(keptLink);
    const handle = kept || wanted.handle;
    if (!handle) return;

    /* A kept link may name the variant (the colour); its size is the kept size. */
    let keptVariant = '';
    try {
      keptVariant = new URL(keptLink, window.location.origin).searchParams.get('variant') ?? '';
    } catch {
      keptVariant = '';
    }
    this.#load(handle, kept ? { variant: /^\d{1,20}$/.test(keptVariant) ? keptVariant : '', size: wanted.size } : wanted, false);
  }

  #topicCode() {
    if (!(this.topic instanceof HTMLSelectElement)) return '';
    return (this.topic.selectedOptions[0]?.dataset.topic ?? '').toLowerCase();
  }

  /* No topic select: always shown. Hidden parts are disabled so they are not sent. */
  #sync() {
    const code = this.#topicCode();
    const shown = !(this.topic instanceof HTMLSelectElement) || this.topics.includes(code);
    const required = shown && this.requiredTopics.includes(code);

    this.root.hidden = !shown;
    if (this.input instanceof HTMLInputElement) {
      this.input.disabled = !shown;
      this.input.required = required && !this.hasProduct;
    }
    if (this.requiredMark) this.requiredMark.hidden = !required;
    if (this.link instanceof HTMLInputElement) this.link.disabled = !shown || !this.hasProduct;
    if (this.size instanceof HTMLSelectElement) this.size.disabled = !shown || this.sizeField?.hidden !== false;
    if (!shown) this.#close();
  }

  #onInput() {
    window.clearTimeout(this.timer);
    const text = this.input.value.trim();

    const handle = handleFromLink(text);
    if (handle) {
      this.#close();
      this.#load(handle, { variant: '', size: '' }, true);
      return;
    }
    if (text.length < 2) {
      this.#close();
      return;
    }
    this.timer = window.setTimeout(() => this.#search(text), SEARCH_DELAY);
  }

  /** @param {string} text */
  async #search(text) {
    this.searchRequest?.abort();
    this.searchRequest = new AbortController();
    const query = new URLSearchParams({
      q: text,
      'resources[type]': 'product',
      'resources[limit]': String(SEARCH_LIMIT),
      'resources[options][unavailable_products]': 'last',
    });

    let products = [];
    try {
      const response = await fetch(`${routesRoot()}search/suggest.json?${query}`, {
        signal: this.searchRequest.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const data = await response.json();
      products = data?.resources?.results?.products ?? [];
    } catch {
      return; /* Aborted by newer typing, or offline: the text is still sent as typed. */
    }
    if (this.input.value.trim() !== text) return;

    this.results = products
      .filter((product) => typeof product?.handle === 'string' && PRODUCT_HANDLE.test(product.handle))
      .map((product) => ({
        handle: product.handle,
        title: String(product.title ?? product.handle),
        image: imageAt(product.featured_image?.url ?? product.image, 96),
        available: product.available !== false,
      }));
    this.#render();
  }

  #render() {
    this.list.replaceChildren(...this.results.map((result, index) => {
      const option = document.createElement('li');
      option.id = `${this.list.id}-${index}`;
      option.className = 'lm-contact-product__option';
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.dataset.index = String(index);

      const image = document.createElement('img');
      image.className = 'lm-contact-product__thumb';
      image.alt = '';
      image.width = 32;
      image.height = 48;
      image.loading = 'lazy';
      if (result.image) image.src = result.image;
      else image.hidden = true;

      const name = document.createElement('span');
      name.textContent = result.title;
      option.append(image, name);
      return option;
    }));

    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    const open = this.results.length > 0;
    this.list.hidden = !open;
    this.input.setAttribute('aria-expanded', String(open));

    const count = this.results.length;
    let message = this.root.dataset.resultsNone ?? '';
    if (count === 1) message = this.root.dataset.resultsOne ?? '';
    else if (count > 1) message = (this.root.dataset.resultsOther ?? '').replace('[count]', String(count));
    if (this.status) this.status.textContent = message;
  }

  #close() {
    if (!(this.list instanceof HTMLElement)) return;
    this.list.hidden = true;
    this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
    this.active = -1;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    const open = !this.list.hidden && this.results.length > 0;
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      this.#close();
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#highlight((this.active + step + this.results.length) % this.results.length);
    } else if (event.key === 'Enter' && this.active >= 0) {
      /* Choosing a suggestion, not sending the form. */
      event.preventDefault();
      this.#pick(this.active, true);
    }
  }

  /** @param {number} index */
  #highlight(index) {
    this.active = index;
    for (const option of this.list.children) {
      const selected = option.id === `${this.list.id}-${index}`;
      option.setAttribute('aria-selected', String(selected));
      if (selected && option instanceof HTMLElement) this.#keepInList(option);
    }
    this.input.setAttribute('aria-activedescendant', `${this.list.id}-${index}`);
  }

  /**
   * Scroll the list, never the window, so the highlighted option is in view.
   * @param {HTMLElement} option
   */
  #keepInList(option) {
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (top < this.list.scrollTop) this.list.scrollTop = top;
    else if (bottom > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = bottom - this.list.clientHeight;
  }

  /**
   * @param {number} index
   * @param {boolean} byVisitor
   */
  #pick(index, byVisitor) {
    const result = this.results[index];
    if (!result) return;
    this.#close();
    this.input.value = result.title;
    this.#load(result.handle, { variant: '', size: '' }, byVisitor);
  }

  /**
   * Look the product up by handle and show it as chosen. If it does not exist,
   * whatever is in the text field is sent as typed.
   * @param {string} handle
   * @param {{ variant: string, size: string }} wanted
   * @param {boolean} byVisitor - move focus on, because the text field hides
   */
  async #load(handle, wanted, byVisitor) {
    /* A choice supersedes any search still waiting or running. */
    window.clearTimeout(this.timer);
    this.searchRequest?.abort();
    this.loadRequest?.abort();
    this.loadRequest = new AbortController();
    let product = null;
    try {
      const response = await fetch(`${routesRoot()}products/${handle}.js`, {
        signal: this.loadRequest.signal,
        headers: { Accept: 'application/json' },
      });
      if (response.ok) product = await response.json();
    } catch {
      return;
    }
    if (!product || typeof product.title !== 'string') return;

    const variants = Array.isArray(product.variants) ? product.variants : [];
    this.product = product;
    this.baseVariant = variants.find((variant) => String(variant.id) === wanted.variant) ?? null;
    this.handle = handle;

    this.hasProduct = true;
    this.input.value = product.title;
    this.title.textContent = product.title;
    const src = imageAt(product.featured_image, 128);
    if (this.image instanceof HTMLImageElement) {
      this.image.hidden = src === '';
      if (src) this.image.src = src;
    }
    this.search.hidden = true;
    this.chosen.hidden = false;
    this.#fillSizes(product, wanted);
    this.#linkSize();
    this.#sync();

    if (byVisitor) {
      const next = this.sizeField?.hidden === false ? this.size : this.change;
      next?.focus();
    }
  }

  /**
   * The product's Size option, each size marked when no variant of it is
   * available. Products without a Size option get no size field.
   * @param {any} product - Shopify's /products/<handle>.js
   * @param {{ variant: string, size: string }} wanted
   */
  #fillSizes(product, wanted) {
    if (!(this.size instanceof HTMLSelectElement) || !this.sizeField) return;
    this.size.options.length = 1;

    const options = Array.isArray(product.options) ? product.options : [];
    const option = options.find((candidate) => /size/i.test(String(candidate?.name ?? '')));
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!option || !Array.isArray(option.values)) {
      this.sizeField.hidden = true;
      return;
    }

    const key = `option${option.position}`;
    const wantedVariant = variants.find((variant) => String(variant.id) === wanted.variant);
    const wantedSize = (wantedVariant?.[key] ?? wanted.size).toLowerCase();
    const soldOut = this.root.dataset.soldOut ?? '[size]';

    for (const value of option.values) {
      const name = String(value);
      const available = variants.some((variant) => variant[key] === name && variant.available);
      const entry = new Option(available ? name : soldOut.replace('[size]', name), name);
      entry.selected = name.toLowerCase() === wantedSize;
      this.size.add(entry);
    }
    this.sizeField.hidden = false;
  }

  /**
   * The product's address, with ?variant= for the chosen size in the colour the
   * product page link named; without a size (or a colour to keep), no variant.
   * So the link never names a different size from the Size field.
   */
  #linkSize() {
    if (!this.product || !this.handle) return;
    const url = new URL(`${routesRoot()}products/${this.handle}`, window.location.origin);

    const size = this.size instanceof HTMLSelectElement ? this.size.value : '';
    const options = Array.isArray(this.product.options) ? this.product.options : [];
    const sizeOption = options.find((candidate) => /size/i.test(String(candidate?.name ?? '')));
    const base = this.baseVariant;
    if (base && !sizeOption) {
      url.searchParams.set('variant', String(base.id));
    } else if (base && size) {
      const key = `option${sizeOption.position}`;
      const variants = Array.isArray(this.product.variants) ? this.product.variants : [];
      const match = variants.find((variant) => variant[key] === size
        && [1, 2, 3].every((n) => n === sizeOption.position || variant[`option${n}`] === base[`option${n}`]));
      if (match) url.searchParams.set('variant', String(match.id));
    }

    this.link.value = url.href;
    this.title.href = url.href;
  }

  /** @param {boolean} byVisitor */
  #clear(byVisitor) {
    this.hasProduct = false;
    this.product = null;
    this.baseVariant = null;
    this.input.value = '';
    this.link.value = '';
    this.chosen.hidden = true;
    this.search.hidden = false;
    if (this.sizeField) this.sizeField.hidden = true;
    if (this.size instanceof HTMLSelectElement) this.size.options.length = 1;
    this.#sync();
    if (byVisitor) this.input.focus();
  }
}

class LmContactForm extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** The size kept after a failed post, for the product field. */
  #keptSize = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const form = this.querySelector('form');
    const status = this.querySelector('[data-lm-status]');
    const failed = status instanceof HTMLElement && status.dataset.lmStatus === 'error';

    /* After a failed post, put back what the visitor typed; on any other load
       (a new visit or a sent message) forget it. */
    let topicRestored = false;
    if (failed && form) topicRestored = this.#restore(form);
    else this.#forget();
    const wanted = requestedProduct();
    if (!topicRestored) this.#preselectTopic(wanted.handle ? 'product' : '');

    const productField = this.querySelector('[data-lm-product-field]');
    if (productField instanceof HTMLElement) {
      const topic = this.querySelector('select[data-lm-topic]');
      const size = this.#keptSize || wanted.size;
      new LmContactProduct(productField, topic instanceof HTMLSelectElement ? topic : null, signal).start({ ...wanted, size });
    }

    /* Capture phase on the wrapper, so the values are kept before any handler on
       the form itself (Shopify's spam check) can stop the event. */
    this.addEventListener('submit', (event) => {
      if (event.target instanceof HTMLFormElement) this.#remember(event.target);
    }, { signal, capture: true });

    if (status instanceof HTMLElement) this.#focusStatus(status, signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /* Select the topic whose data-topic matches ?topic=, compared as text (no
     selector built from the URL), else the fallback (product, when a product
     page link names a product without a topic). Unknown codes, and a topic the
     visitor already changed, are left alone.
     @param {string} fallback */
  #preselectTopic(fallback) {
    const code = requestedTopic() || fallback;
    if (!code) return;

    const select = this.querySelector('select[data-lm-topic]');
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.selectedIndex > 0) return;

    for (const option of select.options) {
      if ((option.dataset.topic ?? '').toLowerCase() !== code) continue;
      option.selected = true;
      return;
    }
  }

  /** @param {HTMLFormElement} form */
  #remember(form) {
    /** @type {Record<string, string>} */
    const values = {};
    for (const name of [...KEPT_FIELDS, KEPT_SIZE]) {
      const field = form.elements.namedItem(name);
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) values[name] = field.value;
    }

    try {
      tabStorage()?.setItem(KEPT_STORAGE_KEY, JSON.stringify(values));
    } catch {
      /* Storage full or blocked: the form still posts; the fields start again. */
    }
  }

  /**
   * Put the kept values back into the re-rendered form. A topic is restored only
   * if it is still one of the options.
   * @param {HTMLFormElement} form
   * @returns {boolean} whether the topic was restored
   */
  #restore(form) {
    /** @type {unknown} */
    let saved = null;
    try {
      saved = JSON.parse(tabStorage()?.getItem(KEPT_STORAGE_KEY) ?? 'null');
    } catch {
      return false;
    }
    if (!saved || typeof saved !== 'object') return false;

    const size = /** @type {Record<string, unknown>} */ (saved)[KEPT_SIZE];
    if (typeof size === 'string') this.#keptSize = size.slice(0, 40);

    let topicRestored = false;
    for (const name of KEPT_FIELDS) {
      const value = /** @type {Record<string, unknown>} */ (saved)[name];
      if (typeof value !== 'string' || value === '') continue;

      const field = form.elements.namedItem(name);
      if (field instanceof HTMLSelectElement) {
        const option = [...field.options].find((candidate) => candidate.value === value);
        if (option) {
          option.selected = true;
          topicRestored = true;
        }
      } else if (field instanceof HTMLInputElement) {
        field.value = value;
      }
    }
    return topicRestored;
  }

  #forget() {
    try {
      tabStorage()?.removeItem(KEPT_STORAGE_KEY);
    } catch {
      /* Nothing kept, or storage blocked. */
    }
  }

  /**
   * After a post the page reloads at #contact-form. Chrome resets focus while it
   * processes the fragment, so focus the notice after load, and only if the
   * visitor has not focused something else in the meantime.
   * @param {HTMLElement} status
   * @param {AbortSignal} signal
   */
  #focusStatus(status, signal) {
    const focusStatus = () => {
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && active !== document.body) return;
        status.focus();
      });
    };
    if (document.readyState === 'complete') focusStatus();
    else window.addEventListener('load', focusStatus, { once: true, signal });
  }
}

if (!customElements.get('lm-policy-page')) customElements.define('lm-policy-page', LmPolicyPage);
if (!customElements.get('lm-cookie-preferences')) customElements.define('lm-cookie-preferences', LmCookiePreferences);
if (!customElements.get('lm-contact-form')) customElements.define('lm-contact-form', LmContactForm);
